import React, { ReactNode, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { authState, User } from 'states';
import { RouteConstants, isAdminConsoleUser } from './constants';
import { isPasswordChangeRequired } from './helpers';
import { Login, ForgotPassword, ResetPassword, ChangePassword } from './components/pages/index';

// Code spiltting
const Home = React.lazy(() =>
  import('./components/pages/index').then(({ Home }) => ({
    default: Home,
  }))
);

const VisualizationHome = React.lazy(() =>
  import('./components/pages/index').then(({ VisualizationHome }) => ({
    default: VisualizationHome,
  }))
);

const UsersHome = React.lazy(() =>
  import('./components/pages/index').then(({ UsersHome }) => ({
    default: UsersHome,
  }))
);

const RolesHome = React.lazy(() =>
  import('./components/pages/index').then(({ RolesHome }) => ({
    default: RolesHome,
  }))
);

const Router = () => {
  const auth = useRecoilValue(authState);
  return (
    <Suspense fallback={<div className=""></div>}>
      <BrowserRouter>
        <Routes>
          <Route path={RouteConstants.login} element={<Login />} />
          <Route path={RouteConstants.root} element={<Login />} />
          <Route path={RouteConstants.forgotPassword} element={<ForgotPassword />} />
          <Route path={RouteConstants.resetPassword} element={<ResetPassword />} />

          <Route element={<ProtectedRoute auth={auth} />}>
            <Route path={RouteConstants.dashboards} element={<Home />} />
            <Route path={RouteConstants.visualizations} element={<VisualizationHome />} />
            <Route path={RouteConstants.users} element={<UsersHome />} />
            <Route path={RouteConstants.roles} element={<RolesHome />} />
            <Route path={RouteConstants.changePassword} element={<ChangePassword />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </Suspense>
  );
};

interface ProtectedRouteProps {
  auth: User;
  redirectPath?: string;
  children?: ReactNode;
}

const ProtectedRoute = ({
  auth,
  redirectPath = RouteConstants.root,
  children,
}: ProtectedRouteProps) => {
  const location = useLocation();

  if (!Object.keys(auth).length) {
    return <Navigate to={redirectPath} />;
  }

  if (!isAdminConsoleUser(auth)) {
    return <Navigate to={RouteConstants.login} state={{ accessDenied: true }} />;
  }

  // Still on the default password: only Change password is reachable until it's updated.
  if (isPasswordChangeRequired(auth) && location.pathname !== RouteConstants.changePassword) {
    return <Navigate to={RouteConstants.changePassword} state={{ forced: true }} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};

export default Router;
